import React, { useContext, useState } from 'react';
import { Box, Chip, LinearProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WidgetCard from './WidgetCard';
import { HomeNavigationContext } from './HomeNavigationContext';
import usePermissions from '../../../utils/usePermissions';
import { useWidgetData } from './useWidgetData';

interface SubmissionResponse {
  date: string;
  limit: number;
  total: number;
  filled: number;
  missing: number;
  regions: Array<{ region: string; total: number; missing: number }>;
  missingList: Array<{ username: string; pharmacyNum: string; region: string }>;
}

const ACCENT = '#0369a1';
const dayLabel = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
};

/** Виджет «Сдача отчётов» (/admin): кто из аптек не сдал дневной отчёт. Право: home.reports.submission.view (+ .limit) */
const ReportSubmissionWidget: React.FC = () => {
  const [day, setDay] = useState<'yesterday' | 'today'>('yesterday');
  const { data, loading, error, reload } = useWidgetData<SubmissionResponse>('/api/v2/home/reports/submission', { day });
  const navigate = useContext(HomeNavigationContext);
  const { hasPerm } = usePermissions();
  const canOpenFill = Boolean(navigate) && hasPerm('reports.menu') && hasPerm('reports.fill_monitoring');

  const percent = data && data.total ? Math.round((data.filled / data.total) * 100) : 0;
  const problemRegions = (data?.regions || []).filter(r => r.missing > 0).slice(0, 4);

  return (
    <WidgetCard
      title="Сдача отчётов"
      icon={<FactCheckOutlinedIcon />}
      accent={ACCENT}
      loading={loading}
      error={error}
      onRefresh={reload}
      headerExtra={
        <ToggleButtonGroup
          size="small" exclusive value={day} onChange={(_, v) => v && setDay(v)}
          sx={{ mr: 0.5, '& .MuiToggleButton-root': { py: 0.1, px: 1.1, fontSize: 12, textTransform: 'none' } }}
        >
          <ToggleButton value="yesterday">Вчера</ToggleButton>
          <ToggleButton value="today">Сегодня</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {data && (
        <Box>
          <Box sx={{ px: 2, pt: 1.25, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{data.filled} из {data.total}</Typography>
              <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>аптек сдали отчёт за {dayLabel(data.date)} ({percent}%)</Typography>
            </Box>
            <LinearProgress
              variant="determinate" value={percent}
              sx={{ mt: 0.75, height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: percent === 100 ? '#16a34a' : ACCENT } }}
            />
            {problemRegions.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {problemRegions.map(r => (
                  <Chip key={r.region} size="small" label={`${r.region}: ${r.missing}`} sx={{ height: 22, bgcolor: '#fef2f2', color: '#b91c1c', fontWeight: 500 }} />
                ))}
              </Box>
            )}
          </Box>

          {data.missing === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, color: '#64748b', borderTop: '1px solid #f1f5f9' }}>
              <TaskAltRoundedIcon sx={{ color: '#10b981' }} />
              <Typography sx={{ fontSize: 13 }}>Все аптеки сдали отчёт</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ px: 2, py: 0.75, fontSize: 12, fontWeight: 600, color: '#64748b', bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                Не сдали: {data.missing}{data.missing > data.missingList.length ? ` (показаны первые ${data.missingList.length})` : ''}
              </Typography>
              {data.missingList.map((p, i) => (
                <Box key={p.username} sx={{ px: 2, py: 0.9, display: 'flex', alignItems: 'center', gap: 1.5, borderTop: i ? '1px solid #f1f5f9' : 'none', '&:hover': { bgcolor: '#f8fafc' } }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', minWidth: 78 }}>Аптека № {p.pharmacyNum}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#64748b', flex: 1 }} noWrap>{p.username}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{p.region || '-'}</Typography>
                </Box>
              ))}
              {canOpenFill && (
                <Box
                  onClick={() => navigate && navigate(3, 'empty')}
                  sx={{ px: 2, py: 1, fontSize: 12.5, fontWeight: 600, color: ACCENT, cursor: 'pointer', borderTop: '1px solid #f1f5f9', '&:hover': { bgcolor: '#f0f9ff' } }}
                >
                  Открыть «Заполнение» →
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </WidgetCard>
  );
};

export default ReportSubmissionWidget;
